import React, { useState, useEffect, useCallback } from 'react';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../common/ConfirmDialog';
import {
    Box,
    Button,
    Typography,
    Alert,
    AlertTitle,
    LinearProgress,
    Paper,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    IconButton,
    Skeleton,
    Link
} from '@mui/material';
import {
    CloudUpload as UploadIcon,
    CheckCircle as SuccessIcon,
    Error as ErrorIcon,
    Delete as DeleteIcon,
    InsertDriveFile as FileIcon,
    DateRange as DateRangeIcon,
    OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import axios from 'axios';

const AmexCSVUpload = ({ connectorId, onUploadComplete }) => {
    const { confirmDialogProps, confirm } = useConfirmDialog();
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadResults, setUploadResults] = useState(null);
    const [error, setError] = useState(null);
    const [dateRange, setDateRange] = useState(null);
    const [loadingDateRange, setLoadingDateRange] = useState(true);
    const [deleting, setDeleting] = useState(false);
    const [rewardsSummary, setRewardsSummary] = useState(null);
    const [loadingRewards, setLoadingRewards] = useState(true);
    const [statements, setStatements] = useState([]);
    const [loadingStatements, setLoadingStatements] = useState(true);

    // Fetch existing transaction date range on mount
    useEffect(() => {
        const fetchDateRange = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/connectors/amex/${connectorId}/date-range`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    }
                );
                setDateRange(response.data);
            } catch (err) {
                console.error('Error fetching date range:', err);
            } finally {
                setLoadingDateRange(false);
            }
        };

        if (connectorId) {
            fetchDateRange();
        }
    }, [connectorId]);

    // Fetch rewards summary on mount
    useEffect(() => {
        const fetchRewards = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/connectors/amex/${connectorId}/rewards`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    }
                );
                setRewardsSummary(response.data);
            } catch (err) {
                console.error('Error fetching rewards summary:', err);
            } finally {
                setLoadingRewards(false);
            }
        };

        if (connectorId) {
            fetchRewards();
        }
    }, [connectorId]);

    // Fetch the original uploaded statement files (stored on the CDN)
    const loadStatements = useCallback(async () => {
        if (!connectorId) return;
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/connectors/amex/${connectorId}/statements`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            setStatements(response.data.statements || []);
        } catch (err) {
            console.error('Error fetching statements:', err);
            setStatements([]);
        } finally {
            setLoadingStatements(false);
        }
    }, [connectorId]);

    useEffect(() => {
        loadStatements();
    }, [loadStatements]);

    const handleDeleteAllData = async () => {
        if (!await confirm({ title: 'Delete All Transactions', message: 'This will delete ALL transactions for this connector. This cannot be undone.', confirmText: 'Delete All', severity: 'danger' })) {
            return;
        }

        setDeleting(true);
        setError(null);

        try {
            const token = localStorage.getItem('token');
            const response = await axios.delete(
                `${process.env.REACT_APP_API_URL}/api/connectors/amex/${connectorId}/transactions`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (response.data.success) {
                // Refresh date range to show empty state
                const dateRangeResponse = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/connectors/amex/${connectorId}/date-range`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    }
                );
                setDateRange(dateRangeResponse.data);
                setUploadResults({ message: 'All data deleted successfully. You can now re-upload with corrected files.' });
            }
        } catch (err) {
            console.error('Delete error:', err);
            setError(err.response?.data?.error || err.message || 'Failed to delete transactions');
        } finally {
            setDeleting(false);
        }
    };

    const handleFileSelect = (event) => {
        const files = Array.from(event.target.files);
        setSelectedFiles(prevFiles => [...prevFiles, ...files]);
        setError(null);
        setUploadResults(null);
    };

    const handleRemoveFile = (index) => {
        setSelectedFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
    };

    const handleUpload = async () => {
        if (selectedFiles.length === 0) {
            setError('Please select at least one CSV or PDF file to upload');
            return;
        }

        setUploading(true);
        setError(null);
        setUploadResults(null);

        try {
            const formData = new FormData();
            selectedFiles.forEach(file => {
                formData.append('csvFiles', file);
            });
            formData.append('connectorId', connectorId);

            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/connectors/amex/upload-csv`,
                formData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            setUploadResults(response.data);
            setSelectedFiles([]); // Clear selected files after successful upload

            // Refresh date range after upload
            const dateRangeResponse = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/connectors/amex/${connectorId}/date-range`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            setDateRange(dateRangeResponse.data);

            // Refresh rewards summary after upload
            const rewardsResponse = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/connectors/amex/${connectorId}/rewards`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            setRewardsSummary(rewardsResponse.data);

            // Refresh the saved-statements list so the file just uploaded appears
            await loadStatements();

            if (onUploadComplete) {
                onUploadComplete(response.data);
            }

        } catch (err) {
            console.error('Upload error:', err);
            setError(err.response?.data?.error || err.message || 'Failed to upload CSV files');
        } finally {
            setUploading(false);
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    return (
        <Box>
            <Paper elevation={0} sx={{ p: 3, border: '1px dashed #ccc', backgroundColor: '#fafafa' }}>
                <Typography variant="h6" gutterBottom>
                    Upload American Express Statements
                </Typography>

                {/* Membership Rewards Summary */}
                {loadingRewards ? (
                    <Box sx={{ mb: 3 }}>
                        <Skeleton variant="rectangular" height={100} />
                    </Box>
                ) : rewardsSummary && rewardsSummary.summary && rewardsSummary.summary.currentBalance > 0 ? (
                    <Alert severity="info" sx={{ mb: 3, backgroundColor: '#e3f2fd' }}>
                        <AlertTitle sx={{ fontWeight: 600 }}>🎁 Membership Rewards</AlertTitle>
                        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 1 }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Current Balance</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {rewardsSummary.summary.currentBalance.toLocaleString()} points
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Total Earned</Typography>
                                <Typography variant="body1" sx={{ color: 'success.main', fontWeight: 600 }}>
                                    +{rewardsSummary.summary.totalEarned.toLocaleString()} points
                                </Typography>
                            </Box>
                            {rewardsSummary.summary.totalRedeemed > 0 && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Total Redeemed</Typography>
                                    <Typography variant="body1" sx={{ color: 'error.main', fontWeight: 600 }}>
                                        -{rewardsSummary.summary.totalRedeemed.toLocaleString()} points
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                        {rewardsSummary.summary.rewardsAccountNumber && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                Rewards Account: {rewardsSummary.summary.rewardsAccountNumber}
                            </Typography>
                        )}
                    </Alert>
                ) : null}

                {/* Existing Transaction Date Range & Missing Periods */}
                {loadingDateRange ? (
                    <Box sx={{ mb: 3 }}>
                        <Skeleton variant="rectangular" height={120} />
                    </Box>
                ) : dateRange && dateRange.hasData ? (
                    <>
                        <Alert severity="success" icon={<DateRangeIcon />} sx={{ mb: 2 }}>
                            <AlertTitle>Current Data Coverage</AlertTitle>
                            <Typography variant="body2">
                                <strong>Date Range:</strong> {new Date(dateRange.minDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })} to {new Date(dateRange.maxDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                <strong>Total Transactions:</strong> {dateRange.totalTransactions.toLocaleString()}
                            </Typography>
                            {dateRange.missingMonths && dateRange.missingMonths.length > 0 && (
                                <Typography variant="body2" sx={{ mt: 0.5, color: 'warning.main' }}>
                                    <strong>⚠️ Missing {dateRange.missingMonths.length} month{dateRange.missingMonths.length !== 1 ? 's' : ''}:</strong> {
                                        dateRange.missingMonths.slice(0, 6).map(month => {
                                            const [year, monthNum] = month.split('-');
                                            const date = new Date(parseInt(year), parseInt(monthNum) - 1);
                                            return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
                                        }).join(', ')
                                    }{dateRange.missingMonths.length > 6 ? `, +${dateRange.missingMonths.length - 6} more` : ''}
                                </Typography>
                            )}
                            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    onClick={handleDeleteAllData}
                                    disabled={deleting}
                                    startIcon={<DeleteIcon />}
                                >
                                    {deleting ? 'Deleting...' : 'Clear All Data'}
                                </Button>
                                <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', fontStyle: 'italic' }}>
                                    (Use if dates are wrong)
                                </Typography>
                            </Box>
                        </Alert>

                        {/* Show what periods to upload next */}
                        {(() => {
                            const today = new Date();
                            const maxDate = new Date(dateRange.maxDate);
                            const daysDiff = Math.floor((today - maxDate) / (1000 * 60 * 60 * 24));

                            if (daysDiff > 7) {
                                return (
                                    <Alert severity="warning" sx={{ mb: 3 }}>
                                        <AlertTitle>📅 Upload Recent Transactions</AlertTitle>
                                        <Typography variant="body2">
                                            Your data is <strong>{daysDiff} days behind</strong>. Latest transaction: {new Date(dateRange.maxDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mt: 1 }}>
                                            <strong>Recommended:</strong> Upload statements from {new Date(maxDate.getTime() + 86400000).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })} to today
                                        </Typography>
                                    </Alert>
                                );
                            } else {
                                return (
                                    <Alert severity="info" sx={{ mb: 3 }}>
                                        <AlertTitle>✅ Data is Up to Date</AlertTitle>
                                        <Typography variant="body2">
                                            Latest transaction from {new Date(dateRange.maxDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })} ({daysDiff} {daysDiff === 1 ? 'day' : 'days'} ago)
                                        </Typography>
                                    </Alert>
                                );
                            }
                        })()}
                    </>
                ) : (
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        <AlertTitle>No Data Yet</AlertTitle>
                        <Typography variant="body2">
                            No transactions have been uploaded yet. Upload your first statement CSV below.
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            <strong>Recommended:</strong> Start with your most recent 3-6 months of statements.
                        </Typography>
                    </Alert>
                )}

                {/* Saved statement files (the original PDFs/CSVs, stored on the CDN) */}
                {loadingStatements ? (
                    <Box sx={{ mb: 3 }}>
                        <Skeleton variant="rectangular" height={80} />
                    </Box>
                ) : statements.length > 0 ? (
                    <Paper variant="outlined" sx={{ mb: 3, p: 2 }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FileIcon fontSize="small" /> Saved Statements ({statements.length})
                        </Typography>
                        <List dense>
                            {statements.map((statement, index) => (
                                <ListItem
                                    key={statement.url || index}
                                    disablePadding
                                    secondaryAction={
                                        <IconButton
                                            edge="end"
                                            component="a"
                                            href={statement.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title="Open statement"
                                            aria-label={`Open ${statement.filename}`}
                                        >
                                            <OpenInNewIcon fontSize="small" />
                                        </IconButton>
                                    }
                                >
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                        <FileIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Link href={statement.url} target="_blank" rel="noopener noreferrer" underline="hover">
                                                {statement.filename}
                                            </Link>
                                        }
                                        secondary={[
                                            statement.uploaded_at ? new Date(statement.uploaded_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : null,
                                            statement.size != null ? formatFileSize(statement.size) : null
                                        ].filter(Boolean).join(' · ')}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                ) : null}

                <Alert severity="info" sx={{ mb: 3 }}>
                    <AlertTitle>Manual CSV Upload Required</AlertTitle>
                    We are unable to support automated connection to American Express.
                    Please download your statement CSV files from American Express online
                    banking and upload them here. You can upload multiple files at once.
                </Alert>

                <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>How to download your Amex statements:</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary" component="ol" sx={{ pl: 2 }}>
                        <li>Log in to your American Express account</li>
                        <li>Go to Statements & Activity</li>
                        <li>Select the statement period you want</li>
                        <li>Click "Download" and select "CSV" format (or PDF for older statements)</li>
                        <li>Upload the downloaded CSV or PDF file(s) below</li>
                    </Typography>
                    <Alert severity="info" sx={{ mt: 1 }}>
                        <Typography variant="caption">
                            💡 <strong>Tip:</strong> PDFs are automatically converted to CSV format during upload. The system automatically detects USD and GBP formats - you can upload both in the same batch!
                        </Typography>
                    </Alert>
                </Box>

                {/* File Selection */}
                <Box sx={{ mb: 3 }}>
                    <input
                        accept=".csv,.pdf"
                        style={{ display: 'none' }}
                        id="amex-csv-file-input"
                        multiple
                        type="file"
                        onChange={handleFileSelect}
                    />
                    <label htmlFor="amex-csv-file-input">
                        <Button
                            variant="outlined"
                            component="span"
                            startIcon={<UploadIcon />}
                            disabled={uploading}
                            fullWidth
                        >
                            Select CSV or PDF Files
                        </Button>
                    </label>
                </Box>

                {/* Selected Files List */}
                {selectedFiles.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Selected Files ({selectedFiles.length}):
                        </Typography>
                        <List dense>
                            {selectedFiles.map((file, index) => (
                                <ListItem
                                    key={index}
                                    secondaryAction={
                                        <IconButton
                                            edge="end"
                                            onClick={() => handleRemoveFile(index)}
                                            disabled={uploading}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    }
                                >
                                    <ListItemIcon>
                                        <FileIcon />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={file.name}
                                        secondary={formatFileSize(file.size)}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                )}

                {/* Upload Button */}
                {selectedFiles.length > 0 && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleUpload}
                        disabled={uploading}
                        startIcon={<UploadIcon />}
                        fullWidth
                    >
                        {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} File${selectedFiles.length > 1 ? 's' : ''}`}
                    </Button>
                )}

                {/* Upload Progress */}
                {uploading && (
                    <Box sx={{ mt: 2 }}>
                        <LinearProgress />
                        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                            Processing files...
                        </Typography>
                    </Box>
                )}

                {/* Error Message */}
                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        <AlertTitle>Upload Failed</AlertTitle>
                        {error}
                    </Alert>
                )}

                {/* Success Results */}
                {uploadResults && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                        <AlertTitle>Upload Successful</AlertTitle>
                        <Typography variant="body2">
                            Processed {uploadResults.filesProcessed} file{uploadResults.filesProcessed > 1 ? 's' : ''} and
                            imported {uploadResults.totalTransactions} transaction{uploadResults.totalTransactions !== 1 ? 's' : ''}
                        </Typography>

                        {uploadResults.files && uploadResults.files.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    File Details:
                                </Typography>
                                <List dense>
                                    {uploadResults.files.map((file, index) => (
                                        <ListItem key={index} disablePadding>
                                            <ListItemIcon>
                                                {file.success ? (
                                                    <SuccessIcon color="success" fontSize="small" />
                                                ) : (
                                                    <ErrorIcon color="error" fontSize="small" />
                                                )}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={file.filename}
                                                secondary={
                                                    file.success
                                                        ? `${file.transactions} transactions imported`
                                                        : `Error: ${file.error}`
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        )}
                    </Alert>
                )}

                {/* Information Box */}
                <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        <strong>Supported Formats:</strong> American Express CSV exports (Business and Personal cards)
                        <br />
                        <strong>File Limit:</strong> Up to 10 files per upload, 50MB per file
                        <br />
                        <strong>Statement Storage:</strong> Your uploaded statement files are saved securely and listed above (under "Saved Statements") so you can re-open them anytime
                    </Typography>
                </Box>
            </Paper>
            <ConfirmDialog {...confirmDialogProps} />
        </Box>
    );
};

export default AmexCSVUpload;
